// apps/files-manager/src/components/admin/PropertyForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Loader2, Upload, X, ImagePlus, 
  Youtube, Plus, Trash2, Building2, MapPin, Home, 
  Bed, Bath, Square, Ruler, Shield, Trees, Car, 
  Wifi, Coffee, Users, Dumbbell, Droplet, Zap, Sun,
  Sparkles, AlertCircle, Check, Eye, EyeOff, Map,
  Layers, Building, Store, ChevronDown,
  ChevronUp, HelpCircle, Globe, Link,
  User, Phone, Mail, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

// Slug generator
const generateSlug = (title) => {
  return title.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

// Common amenities list
const COMMON_AMENITIES = [
  { id: 'club_house', label: 'Club House', icon: Users },
  { id: 'cctv', label: 'CCTV Camera', icon: Shield },
  { id: 'gymnasium', label: 'Gymnasium', icon: Dumbbell },
  { id: 'indoor_games', label: 'Indoor Games', icon: Coffee },
  { id: 'jogging_track', label: 'Jogging Track', icon: Ruler },
  { id: 'kids_play', label: 'Kids Play Area', icon: Users },
  { id: 'landscape_garden', label: 'Landscape Garden', icon: Trees },
  { id: 'meditation', label: 'Meditation Area', icon: Sparkles },
  { id: 'park', label: 'Park', icon: Trees },
  { id: 'swimming_pool', label: 'Swimming Pool', icon: Droplet },
  { id: 'reserved_parking', label: 'Reserved Parking', icon: Car },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'sports_facility', label: 'Sports Facility', icon: Dumbbell },
  { id: 'water_storage', label: 'Water Storage', icon: Droplet },
  { id: 'street_lighting', label: 'Street Lighting', icon: Sun },
  { id: 'power_backup', label: 'Power Backup', icon: Zap },
  { id: 'lift', label: 'Lift / Elevator', icon: Building2 },
  { id: 'fire_safety', label: 'Fire Safety', icon: AlertCircle },
  { id: 'wifi', label: 'Wi-Fi Connectivity', icon: Wifi },
];

// Facing Directions
const FACING_DIRECTIONS = [
  'North', 'South', 'East', 'West', 
  'North-East', 'North-West', 'South-East', 'South-West'
];

// Property Categories
const PROPERTY_CATEGORIES = [
  'Residential',
  'Commercial',
  'Industrial',
  'Agricultural'
];

// Property Varieties
const PROPERTY_VARIETIES = [
  'Studio',
  '1 BHK',
  '2 BHK',
  '3 BHK',
  '4 BHK',
  '5 BHK+',
  'Penthouse',
  'Villa',
  'Duplex',
  'Row House',
  'Farm House',
  'Office Space',
  'Retail Shop',
  'Warehouse',
  'Factory',
  'Land'
];

const PropertyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // ?? Default status set to 'active' so new properties appear immediately
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    city: '',
    location: '',
    type: 'flat',
    category: 'Residential',
    status: 'active', // Changed from 'draft' to 'active'
    price: '',
    possession_status: 'under_construction',
    variety: '',
    building_height: '',
    floor_number: '',
    balconies: '',
    carpet_area: '',
    bedrooms: '',
    bathrooms: '',
    servant_room: '0',
    plot_area: '',
    road_width: '',
    facing_direction: '',
    plot_price: '',
    seller_name: '',
    seller_company: '',
    seller_phone: '',
    seller_email: '',
    seller_id: '',
    about_property: '',
    description: '',
    youtube_url: '',
    amenities: [],
    nearby_landmarks: [],
    varieties: [],
    featured_image: null,
    featuredImagePreview: null,
    images: [],
    imagePreviews: [],
    existingImages: [],
    brochure: null,
    brochurePreview: null,
    existingBrochure: null,
    download_count: 0,
    location_map_url: '',
    latitude: '',
    longitude: '',
    google_maps_embed: '',
    faqs: [],
    possession_date: '',
    rera_registered: false,
  });

  useEffect(() => {
    if (isEdit) {
      const fetchProperty = async () => {
        try {
          const record = await pb.collection('properties').getOne(id);
          // Use fallback 'active' if status is missing
          setFormData({
            title: record.title || '',
            slug: record.slug || '',
            city: record.city || '',
            location: record.location || '',
            type: record.type || 'flat',
            category: record.category || 'Residential',
            status: record.status || 'active',
            price: record.price || '',
            possession_status: record.possession_status || 'under_construction',
            variety: record.variety || '',
            building_height: record.building_height || '',
            floor_number: record.floor_number || '',
            balconies: record.balconies || '',
            carpet_area: record.carpet_area || '',
            bedrooms: record.bedrooms || '',
            bathrooms: record.bathrooms || '',
            servant_room: record.servant_room || '0',
            plot_area: record.plot_area || '',
            road_width: record.road_width || '',
            facing_direction: record.facing_direction || '',
            plot_price: record.plot_price || '',
            seller_name: record.seller_name || '',
            seller_company: record.seller_company || '',
            seller_phone: record.seller_phone || '',
            seller_email: record.seller_email || '',
            seller_id: record.seller_id || '',
            about_property: record.about_property || '',
            description: record.description || '',
            youtube_url: record.youtube_url || '',
            amenities: record.amenities || [],
            nearby_landmarks: record.nearby_landmarks || [],
            varieties: record.varieties || [],
            featured_image: null,
            featuredImagePreview: record.featured_image 
              ? pb.files.getURL(record, record.featured_image) 
              : null,
            existingImages: record.images || [],
            images: [],
            imagePreviews: [],
            brochure: null,
            brochurePreview: record.brochure 
              ? pb.files.getURL(record, record.brochure) 
              : null,
            existingBrochure: record.brochure || null,
            download_count: record.download_count || 0,
            location_map_url: record.location_map_url || '',
            latitude: record.latitude || '',
            longitude: record.longitude || '',
            google_maps_embed: record.google_maps_embed || '',
            faqs: record.faqs || [],
            possession_date: record.possession_date || '',
            rera_registered: record.rera_registered || false,
          });
        } catch (error) {
          console.error(error);
          toast.error('Failed to load property');
          navigate('/properties');
        } finally {
          setLoading(false);
        }
      };
      fetchProperty();
    }
  }, [id, isEdit, navigate]);

  // ----- Handlers (unchanged) -----
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleFeaturedImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        featured_image: file,
        featuredImagePreview: URL.createObjectURL(file),
      }));
    }
  };

  const removeFeaturedImage = () => {
    setFormData(prev => ({
      ...prev,
      featured_image: null,
      featuredImagePreview: null,
    }));
  };

  const handleGalleryImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const previews = files.map(file => URL.createObjectURL(file));
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...files],
        imagePreviews: [...prev.imagePreviews, ...previews],
      }));
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => {
      const newImages = [...prev.images];
      const newPreviews = [...prev.imagePreviews];
      newImages.splice(index, 1);
      newPreviews.splice(index, 1);
      return {
        ...prev,
        images: newImages,
        imagePreviews: newPreviews,
      };
    });
  };

  const removeExistingGalleryImage = (index) => {
    setFormData(prev => {
      const newExisting = [...prev.existingImages];
      newExisting.splice(index, 1);
      return { ...prev, existingImages: newExisting };
    });
  };

  const handleBrochureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload PDF or image file (PNG, JPG, WEBP)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB');
        return;
      }
      setFormData(prev => ({
        ...prev,
        brochure: file,
        brochurePreview: URL.createObjectURL(file),
      }));
    }
  };

  const removeBrochure = () => {
    setFormData(prev => ({
      ...prev,
      brochure: null,
      brochurePreview: null,
      existingBrochure: null,
    }));
  };

  const toggleAmenity = (amenityId) => {
    setFormData(prev => {
      const current = prev.amenities || [];
      if (current.includes(amenityId)) {
        return { ...prev, amenities: current.filter(id => id !== amenityId) };
      } else {
        return { ...prev, amenities: [...current, amenityId] };
      }
    });
  };

  const addLandmark = () => {
    const landmark = prompt('Enter nearby landmark (e.g., Metro Station - 2km)');
    if (landmark && landmark.trim()) {
      setFormData(prev => ({
        ...prev,
        nearby_landmarks: [...(prev.nearby_landmarks || []), landmark.trim()]
      }));
    }
  };

  const removeLandmark = (index) => {
    setFormData(prev => ({
      ...prev,
      nearby_landmarks: prev.nearby_landmarks.filter((_, i) => i !== index)
    }));
  };

  const addVariety = () => {
    const variety = prompt('Enter variety details (e.g., 2 BHK - 1200 sq.ft - ?85 Lakhs)');
    if (variety && variety.trim()) {
      setFormData(prev => ({
        ...prev,
        varieties: [...(prev.varieties || []), variety.trim()]
      }));
    }
  };

  const removeVariety = (index) => {
    setFormData(prev => ({
      ...prev,
      varieties: prev.varieties.filter((_, i) => i !== index)
    }));
  };

  const addFAQ = () => {
    const question = prompt('Enter FAQ Question:');
    if (question && question.trim()) {
      const answer = prompt('Enter FAQ Answer:');
      if (answer && answer.trim()) {
        setFormData(prev => ({
          ...prev,
          faqs: [...(prev.faqs || []), { question: question.trim(), answer: answer.trim() }]
        }));
      }
    }
  };

  const removeFAQ = (index) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index)
    }));
  };

  // ----- Submit Handler (ensures status is sent) -----
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Property title is required');
      return;
    }
    if (!formData.city.trim()) {
      toast.error('City is required');
      return;
    }
    if (!formData.location.trim()) {
      toast.error('Location is required');
      return;
    }
    if (!formData.price || Number(formData.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      
      const slug = formData.slug.trim() || generateSlug(formData.title);
      data.append('title', formData.title.trim());
      data.append('slug', slug);
      data.append('city', formData.city.trim());
      data.append('location', formData.location.trim());
      data.append('type', formData.type);
      data.append('category', formData.category);
      data.append('status', formData.status); // now 'active' by default
      data.append('price', Number(formData.price));
      data.append('possession_status', formData.possession_status);
      data.append('variety', formData.variety || '');
      data.append('building_height', formData.building_height || '');
      data.append('floor_number', formData.floor_number || '');
      data.append('balconies', formData.balconies || '');
      data.append('carpet_area', formData.carpet_area || '');
      data.append('bedrooms', formData.bedrooms ? Number(formData.bedrooms) : 0);
      data.append('bathrooms', formData.bathrooms ? Number(formData.bathrooms) : 0);
      data.append('servant_room', formData.servant_room || '0');
      data.append('plot_area', formData.plot_area || '');
      data.append('road_width', formData.road_width || '');
      data.append('facing_direction', formData.facing_direction || '');
      data.append('plot_price', formData.plot_price || '');
      data.append('seller_name', formData.seller_name || '');
      data.append('seller_company', formData.seller_company || '');
      data.append('seller_phone', formData.seller_phone || '');
      data.append('seller_email', formData.seller_email || '');
      data.append('seller_id', formData.seller_id || '');
      data.append('about_property', formData.about_property || '');
      data.append('description', formData.description || '');
      data.append('youtube_url', formData.youtube_url || '');
      data.append('possession_date', formData.possession_date || '');
      data.append('rera_registered', formData.rera_registered);
      data.append('amenities', JSON.stringify(formData.amenities || []));
      data.append('nearby_landmarks', JSON.stringify(formData.nearby_landmarks || []));
      data.append('varieties', JSON.stringify(formData.varieties || []));
      data.append('faqs', JSON.stringify(formData.faqs || []));
      data.append('location_map_url', formData.location_map_url || '');
      data.append('latitude', formData.latitude || '');
      data.append('longitude', formData.longitude || '');
      data.append('google_maps_embed', formData.google_maps_embed || '');
      
      if (formData.featured_image instanceof File) {
        data.append('featured_image', formData.featured_image);
      }
      formData.images.forEach((file) => {
        data.append('images', file);
      });
      if (formData.brochure instanceof File) {
        data.append('brochure', formData.brochure);
      }

      if (isEdit) {
        await pb.collection('properties').update(id, data);
        toast.success('Property updated successfully! ?');
      } else {
        await pb.collection('properties').create(data);
        toast.success('Property created successfully! ??');
      }
      navigate('/properties');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const isFlat = formData.type === 'flat';
  const isPlot = formData.type === 'plot';

  // ----- JSX Rendering (unchanged) -----
  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/properties')} 
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">
              {isEdit ? 'Edit Property' : 'Add New Property'}
            </h1>
            <p className="text-sm text-slate-500">
              {isEdit ? 'Update your property details' : 'List your property for sale'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            formData.status === 'active' ? 'bg-green-100 text-green-700' :
            formData.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
            'bg-slate-100 text-slate-700'
          }`}>
            {formData.status.charAt(0).toUpperCase() + formData.status.slice(1)}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-4">
          {[
            { id: 'basic', label: 'Basic Info', icon: Home },
            { id: 'details', label: 'Details', icon: Building2 },
            { id: 'varieties', label: 'Varieties', icon: Layers },
            { id: 'amenities', label: 'Amenities', icon: Sparkles },
            { id: 'media', label: 'Media', icon: ImagePlus },
            { id: 'location', label: 'Location', icon: Map },
            { id: 'seller', label: 'Seller', icon: User },
            { id: 'faq', label: 'FAQ', icon: HelpCircle },
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-amber-100 text-amber-700'
                  : 'hover:bg-slate-100 text-slate-600'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          
          {/* TAB 1: BASIC INFO */}
          {activeTab === 'basic' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Property Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                    placeholder="e.g. 3BHK Luxury Apartment"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Slug (URL)
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                    placeholder="Auto-generated from title"
                  />
                  <p className="text-xs text-slate-400 mt-1">Leave blank to auto-generate</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                    placeholder="e.g. Mumbai"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Location / Area <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                    placeholder="e.g. Andheri East"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Property Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition bg-white"
                  >
                    <option value="flat">Flat / Apartment</option>
                    <option value="plot">Plot / Land</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition bg-white"
                  >
                    {PROPERTY_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition bg-white"
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Possession Status
                  </label>
                  <select
                    name="possession_status"
                    value={formData.possession_status}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition bg-white"
                  >
                    <option value="under_construction">Under Construction</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="ready_to_move">Ready to Move</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Price (?) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">?</span>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className="w-full pl-8 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                      placeholder="e.g. 8500000"
                      min="0"
                      step="1000"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Possession Date
                  </label>
                  <input
                    type="date"
                    name="possession_date"
                    value={formData.possession_date}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Property Variety
                  </label>
                  <select
                    name="variety"
                    value={formData.variety}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition bg-white"
                  >
                    <option value="">Select Variety</option>
                    {PROPERTY_VARIETIES.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="checkbox"
                    name="rera_registered"
                    checked={formData.rera_registered}
                    onChange={handleChange}
                    className="w-4 h-4 text-amber-500 rounded border-slate-300 focus:ring-amber-500"
                  />
                  <label className="text-sm text-slate-700">
                    RERA Registered Property
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: DETAILS */}
          {activeTab === 'details' && (
            <div className="space-y-5">
              {isFlat && (
                <>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Apartment Details
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Building Height <span className="text-xs text-slate-400">(e.g. G+4)</span>
                      </label>
                      <input
                        type="text"
                        name="building_height"
                        value={formData.building_height}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                        placeholder="e.g. G+4"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Floor Number <span className="text-xs text-slate-400">(e.g. 3rd)</span>
                      </label>
                      <input
                        type="text"
                        name="floor_number"
                        value={formData.floor_number}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                        placeholder="e.g. 3rd"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Number of Balconies
                      </label>
                      <select
                        name="balconies"
                        value={formData.balconies}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition bg-white"
                      >
                        <option value="">Select</option>
                        <option value="0">0</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Carpet Area <span className="text-xs text-slate-400">(sq.ft.)</span>
                      </label>
                      <input
                        type="text"
                        name="carpet_area"
                        value={formData.carpet_area}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                        placeholder="e.g. 1200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Servant Room
                      </label>
                      <select
                        name="servant_room"
                        value={formData.servant_room}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition bg-white"
                      >
                        <option value="0">No</option>
                        <option value="1">Yes</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Bedrooms (BHK)
                      </label>
                      <select
                        name="bedrooms"
                        value={formData.bedrooms}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition bg-white"
                      >
                        <option value="">Select</option>
                        <option value="1">1 BHK</option>
                        <option value="2">2 BHK</option>
                        <option value="3">3 BHK</option>
                        <option value="4">4 BHK</option>
                        <option value="5">5 BHK+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Bathrooms
                      </label>
                      <select
                        name="bathrooms"
                        value={formData.bathrooms}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition bg-white"
                      >
                        <option value="">Select</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4+</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {isPlot && (
                <>
                  <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-green-700 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Plot Details
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Plot Area <span className="text-xs text-slate-400">(sq.ft.)</span>
                      </label>
                      <input
                        type="text"
                        name="plot_area"
                        value={formData.plot_area}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                        placeholder="e.g. 2000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Road Width <span className="text-xs text-slate-400">(feet)</span>
                      </label>
                      <input
                        type="text"
                        name="road_width"
                        value={formData.road_width}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                        placeholder="e.g. 30"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Facing Direction
                      </label>
                      <select
                        name="facing_direction"
                        value={formData.facing_direction}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition bg-white"
                      >
                        <option value="">Select Direction</option>
                        {FACING_DIRECTIONS.map(dir => (
                          <option key={dir} value={dir}>{dir}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Plot Price (?) <span className="text-xs text-slate-400">(per sq.ft.)</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">?</span>
                        <input
                          type="number"
                          name="plot_price"
                          value={formData.plot_price}
                          onChange={handleChange}
                          className="w-full pl-8 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                          placeholder="e.g. 5000"
                          min="0"
                          step="100"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="mt-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  About Property <span className="text-xs text-slate-400">(Brief description)</span>
                </label>
                <textarea
                  name="about_property"
                  rows="2"
                  value={formData.about_property}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                  placeholder="Write a short description about this property..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Detailed Description
                </label>
                <textarea
                  name="description"
                  rows="4"
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                  placeholder="Write a detailed description of the property..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nearby Landmarks
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(formData.nearby_landmarks || []).map((landmark, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm"
                    >
                      {landmark}
                      <button
                        type="button"
                        onClick={() => removeLandmark(index)}
                        className="ml-1 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addLandmark}
                  className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add Landmark
                </button>
              </div>
            </div>
          )}

          {/* TAB 3: VARIETIES */}
          {activeTab === 'varieties' && (
            <div className="space-y-5">
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-purple-700 flex items-center gap-2">
                  <Layers className="w-4 h-4" /> Property Varieties
                </h3>
                <p className="text-xs text-purple-600 mt-1">
                  Add multiple varieties for the same location (e.g., different sizes and prices)
                </p>
              </div>

              <div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(formData.varieties || []).map((variety, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-3 py-2 rounded-lg text-sm border border-purple-200"
                    >
                      {variety}
                      <button
                        type="button"
                        onClick={() => removeVariety(index)}
                        className="ml-1 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addVariety}
                  className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1 border border-purple-200 rounded-lg px-4 py-2 hover:bg-purple-50 transition"
                >
                  <Plus className="w-4 h-4" /> Add Variety
                </button>
                <p className="text-xs text-slate-400 mt-2">
                  Format: 2 BHK - 1200 sq.ft - ?85 Lakhs (Include all details)
                </p>
              </div>
            </div>
          )}

          {/* TAB 4: AMENITIES */}
          {activeTab === 'amenities' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Select Amenities
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {COMMON_AMENITIES.map((amenity) => {
                    const isSelected = (formData.amenities || []).includes(amenity.id);
                    const Icon = amenity.icon;
                    return (
                      <button
                        key={amenity.id}
                        type="button"
                        onClick={() => toggleAmenity(amenity.id)}
                        className={`p-3 rounded-lg border-2 transition-all text-left flex items-center gap-3 ${
                          isSelected
                            ? 'border-amber-500 bg-amber-50 text-amber-700'
                            : 'border-slate-200 hover:border-slate-300 text-slate-600'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-amber-500' : 'text-slate-400'}`} />
                        <span className="text-sm font-medium">{amenity.label}</span>
                        {isSelected && <Check className="w-4 h-4 text-amber-500 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  YouTube Video Tour
                </label>
                <div className="flex items-center gap-2">
                  <Youtube className="w-5 h-5 text-red-500" />
                  <input
                    type="url"
                    name="youtube_url"
                    value={formData.youtube_url}
                    onChange={handleChange}
                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Add a video tour of the property</p>
              </div>
            </div>
          )}

          {/* TAB 5: MEDIA */}
          {activeTab === 'media' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Featured Image <span className="text-red-500">*</span>
                </label>
                {formData.featuredImagePreview ? (
                  <div className="relative inline-block">
                    <img
                      src={formData.featuredImagePreview}
                      alt="Featured"
                      className="h-48 w-auto rounded-lg object-cover border-2 border-amber-200"
                    />
                    <button
                      type="button"
                      onClick={removeFeaturedImage}
                      className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition">
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-500">Click to upload featured image</span>
                    <span className="text-xs text-slate-400">PNG, JPG, WEBP (Max 5MB)</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFeaturedImageChange}
                    />
                  </label>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Gallery Images <span className="text-xs text-slate-400">(Multiple images)</span>
                </label>

                {isEdit && formData.existingImages.length > 0 && (
                  <div className="flex flex-wrap gap-3 mb-3">
                    {formData.existingImages.map((img, idx) => {
                      const url = pb.files.getURL({ id, collectionId: 'properties' }, img);
                      return (
                        <div key={`existing-${idx}`} className="relative group">
                          <img src={url} className="h-24 w-24 object-cover rounded-lg border-2 border-slate-200" />
                          <button
                            type="button"
                            onClick={() => removeExistingGalleryImage(idx)}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition shadow-lg opacity-0 group-hover:opacity-100"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {formData.imagePreviews.length > 0 && (
                  <div className="flex flex-wrap gap-3 mb-3">
                    {formData.imagePreviews.map((preview, idx) => (
                      <div key={`preview-${idx}`} className="relative group">
                        <img src={preview} className="h-24 w-24 object-cover rounded-lg border-2 border-amber-200" />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(idx)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition shadow-lg opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <label className="flex items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition">
                  <Plus className="w-5 h-5 text-slate-400 mr-2" />
                  <span className="text-sm text-slate-500">Add gallery images</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleGalleryImagesChange}
                  />
                </label>
                <p className="text-xs text-slate-400 mt-1">Select multiple images for the property gallery</p>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Brochure <span className="text-xs text-slate-400">(PDF or Image)</span>
                  {formData.download_count > 0 && (
                    <span className="ml-2 text-xs text-amber-600">
                      Downloaded {formData.download_count} times
                    </span>
                  )}
                </label>
                
                {formData.brochurePreview || formData.existingBrochure ? (
                  <div className="relative inline-block">
                    {formData.brochurePreview ? (
                      formData.brochure && formData.brochure.type === 'application/pdf' ? (
                        <div className="flex items-center gap-3 bg-slate-100 rounded-lg p-4 border-2 border-amber-200">
                          <FileText className="w-10 h-10 text-red-500" />
                          <div>
                            <p className="text-sm font-medium text-slate-700">{formData.brochure.name}</p>
                            <p className="text-xs text-slate-400">{(formData.brochure.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={formData.brochurePreview}
                          alt="Brochure"
                          className="h-32 w-auto rounded-lg object-cover border-2 border-amber-200"
                        />
                      )
                    ) : formData.existingBrochure && (
                      <div className="flex items-center gap-3 bg-slate-100 rounded-lg p-4 border-2 border-amber-200">
                        <FileText className="w-10 h-10 text-red-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-700">Brochure</p>
                          <p className="text-xs text-slate-400">Existing file</p>
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={removeBrochure}
                      className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition">
                    <FileText className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-500">Upload Brochure</span>
                    <span className="text-xs text-slate-400">PDF, PNG, JPG, WEBP (Max 10MB)</span>
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      className="hidden"
                      onChange={handleBrochureChange}
                    />
                  </label>
                )}
              </div>
            </div>
          )}

          {/* TAB 6: LOCATION */}
          {activeTab === 'location' && (
            <div className="space-y-5">
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-indigo-700 flex items-center gap-2">
                  <Map className="w-4 h-4" /> Location Details
                </h3>
                <p className="text-xs text-indigo-600 mt-1">
                  Add map location to show property on Google Maps
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Google Maps Embed URL
                </label>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-slate-400" />
                  <input
                    type="url"
                    name="google_maps_embed"
                    value={formData.google_maps_embed}
                    onChange={handleChange}
                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                    placeholder="https://www.google.com/maps/embed?pb=..."
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Paste the embed URL from Google Maps</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Latitude
                  </label>
                  <input
                    type="text"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                    placeholder="e.g. 22.5726"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Longitude
                  </label>
                  <input
                    type="text"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                    placeholder="e.g. 88.3639"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Location Map URL
                </label>
                <div className="flex items-center gap-2">
                  <Link className="w-4 h-4 text-slate-400" />
                  <input
                    type="url"
                    name="location_map_url"
                    value={formData.location_map_url}
                    onChange={handleChange}
                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                    placeholder="https://maps.google.com/?q=22.5726,88.3639"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Direct Google Maps URL for the property location</p>
              </div>
            </div>
          )}

          {/* TAB 7: SELLER */}
          {activeTab === 'seller' && (
            <div className="space-y-5">
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-purple-700 flex items-center gap-2">
                  <User className="w-4 h-4" /> Seller / Owner Details
                </h3>
                <p className="text-xs text-purple-600 mt-1">Properties from same seller will be grouped together</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Seller/Person Name <span className="text-xs text-slate-400">(e.g. John Doe)</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      name="seller_name"
                      value={formData.seller_name}
                      onChange={handleChange}
                      className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                      placeholder="e.g. John Doe"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Seller ID <span className="text-xs text-slate-400">(Unique ID for tracking)</span>
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      name="seller_id"
                      value={formData.seller_id}
                      onChange={handleChange}
                      className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                      placeholder="e.g. SELLER001"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Use same ID to group properties from same seller</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Company Name <span className="text-xs text-slate-400">(if applicable)</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    name="seller_company"
                    value={formData.seller_company}
                    onChange={handleChange}
                    className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                    placeholder="e.g. ABC Properties Pvt Ltd"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="tel"
                      name="seller_phone"
                      value={formData.seller_phone}
                      onChange={handleChange}
                      className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                      placeholder="e.g. +91 98765 43210"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      name="seller_email"
                      value={formData.seller_email}
                      onChange={handleChange}
                      className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none transition"
                      placeholder="e.g. john@example.com"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 8: FAQ */}
          {activeTab === 'faq' && (
            <div className="space-y-5">
              <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-orange-700 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4" /> Frequently Asked Questions
                </h3>
                <p className="text-xs text-orange-600 mt-1">
                  Add FAQs to help buyers understand the property better
                </p>
              </div>

              <div>
                <div className="space-y-3 mb-4">
                  {(formData.faqs || []).map((faq, index) => (
                    <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-slate-800 text-sm">Q: {faq.question}</p>
                          <p className="text-slate-600 text-sm mt-1">A: {faq.answer}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFAQ(index)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addFAQ}
                  className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1 border border-orange-200 rounded-lg px-4 py-2 hover:bg-orange-50 transition"
                >
                  <Plus className="w-4 h-4" /> Add FAQ
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={() => navigate('/properties')}
            className="px-6 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 bg-amber-500 text-white px-8 py-2.5 rounded-lg hover:bg-amber-600 transition disabled:opacity-70 shadow-lg shadow-amber-500/25"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            <Save className="w-4 h-4" /> 
            {isEdit ? 'Update Property' : 'Create Property'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PropertyForm;